/**
 * Shared Drawer Component
 * Handles drawer opening, closing, scroll locking, sticky header alignment,
 * accessible attributes, keyboard navigation (Escape), and focus management.
 *
 * Used by CartDrawer, SearchDrawer, and other drawer interfaces.
 */

class Drawer {
  static instances = new Map();
  static openDrawers = new Set();
  static isGlobalBound = false;

  constructor(elementOrId, options = {}) {
    this.element = typeof elementOrId === 'string'
      ? document.getElementById(elementOrId)
      : elementOrId;

    if (!this.element) return;

    this.id = this.element.id;
    this.options = Object.assign(
      {
        name: this.id.replace(/-drawer$/, ''),
        triggerSelector: `[data-${this.id}-toggle], [aria-controls="${this.id}"]`,
        closeSelector: `[data-${this.id}-close], [data-drawer-close], .drawer__close, .drawer__overlay, .${this.id}__close, .${this.id}__overlay`,
        focusElement: null,
        restoreFocus: true,
        updatePositionOnOpen: true,
        onOpen: null,
        onClose: null,
      },
      options
    );

    this.previousActiveElement = null;
    this._rafId = null;

    // Register instance
    Drawer.instances.set(this.id, this);

    // Initial binding
    this.init();
  }

  static get(id) {
    return Drawer.instances.get(id);
  }

  static lockScroll() {
    document.documentElement.classList.add('lock-scroll');
    document.body.classList.add('lock-scroll');
  }

  static unlockScroll() {
    // Only unlock if no registered drawers (or legacy drawers) are still open
    if (Drawer.openDrawers.size === 0) {
      const anyOtherOpen = document.querySelectorAll('.drawer[open], .drawer.is-open').length > 0;
      if (!anyOtherOpen) {
        document.documentElement.classList.remove('lock-scroll');
        document.body.classList.remove('lock-scroll');
      }
    }
  }

  static bindGlobalListeners() {
    if (Drawer.isGlobalBound) return;
    Drawer.isGlobalBound = true;

    // Close topmost drawer on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && Drawer.openDrawers.size > 0) {
        // Get the most recently opened drawer
        const openList = Array.from(Drawer.openDrawers);
        const topDrawer = openList[openList.length - 1];
        if (topDrawer) {
          topDrawer.close();
        }
      }
    });

    // Prevent scrolling behind open drawers when interacting with backdrop overlays
    document.addEventListener(
      'wheel',
      (e) => {
        if (e.target.closest('.drawer__overlay, .mobile-filter-drawer__overlay')) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    document.addEventListener(
      'touchmove',
      (e) => {
        if (e.target.closest('.drawer__overlay, .mobile-filter-drawer__overlay')) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    // Throttled recalculation of open drawer positions on resize
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (Drawer.openDrawers.size === 0) return;
      if (resizeTimer) cancelAnimationFrame(resizeTimer);
      resizeTimer = requestAnimationFrame(() => {
        Drawer.openDrawers.forEach((drawer) => drawer.updatePosition());
      });
    });
  }

  init() {
    Drawer.bindGlobalListeners();
    this.bindEvents();
    this.updateTriggersAria(false);
  }

  get isOpen() {
    return this.element.classList.contains('is-open') || this.element.hasAttribute('open');
  }

  updatePosition() {
    const headerWrapper =
      document.getElementById('HeaderStickyWrapper') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('header');

    if (headerWrapper) {
      const rect = headerWrapper.getBoundingClientRect();
      const topOffset = Math.max(0, Math.round(rect.bottom));

      // Set standard CSS custom property
      this.element.style.setProperty('--drawer-top', `${topOffset}px`);

      // Set legacy custom properties for complete backwards-compatibility
      if (this.options.name) {
        this.element.style.setProperty(`--${this.options.name}-drawer-top`, `${topOffset}px`);
      }
    }
  }

  open() {
    if (this.isOpen) return;

    this.previousActiveElement = document.activeElement;

    if (this.options.updatePositionOnOpen) {
      this.updatePosition();
    }

    // Set open attributes & classes
    this.element.setAttribute('open', '');
    this.element.classList.add('is-open');
    this.element.setAttribute('aria-hidden', 'false');

    // Update triggers
    this.updateTriggersAria(true);

    // Register as open & lock scroll
    Drawer.openDrawers.add(this);
    Drawer.lockScroll();

    // Custom events
    this.element.dispatchEvent(
      new CustomEvent('drawer:open', { bubbles: true, detail: { drawer: this } })
    );

    // Callback
    if (typeof this.options.onOpen === 'function') {
      this.options.onOpen(this);
    }

    // Accessible focus management
    if (this.options.focusElement) {
      const target =
        typeof this.options.focusElement === 'string'
          ? this.element.querySelector(this.options.focusElement)
          : this.options.focusElement;

      if (target && typeof target.focus === 'function') {
        setTimeout(() => target.focus(), 80);
      }
    }

    this.element.dispatchEvent(
      new CustomEvent('drawer:opened', { bubbles: true, detail: { drawer: this } })
    );
  }

  close() {
    if (!this.isOpen) return;

    this.element.removeAttribute('open');
    this.element.classList.remove('is-open');
    this.element.setAttribute('aria-hidden', 'true');

    // Update triggers
    this.updateTriggersAria(false);

    // Unregister & check scroll
    Drawer.openDrawers.delete(this);
    Drawer.unlockScroll();

    // Custom events
    this.element.dispatchEvent(
      new CustomEvent('drawer:close', { bubbles: true, detail: { drawer: this } })
    );

    // Callback
    if (typeof this.options.onClose === 'function') {
      this.options.onClose(this);
    }

    // Restore focus
    if (
      this.options.restoreFocus &&
      this.previousActiveElement &&
      typeof this.previousActiveElement.focus === 'function' &&
      document.contains(this.previousActiveElement)
    ) {
      this.previousActiveElement.focus();
    }

    this.element.dispatchEvent(
      new CustomEvent('drawer:closed', { bubbles: true, detail: { drawer: this } })
    );
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  updateTriggersAria(isExpanded) {
    if (!this.options.triggerSelector) return;
    const val = isExpanded ? 'true' : 'false';
    document.querySelectorAll(this.options.triggerSelector).forEach((btn) => {
      btn.setAttribute('aria-expanded', val);
    });
  }

  bindEvents() {
    // Delegated click handling for triggers and close buttons
    document.addEventListener('click', (e) => {
      // 1. Check if clicking this drawer's trigger
      const trigger = e.target.closest(this.options.triggerSelector);
      if (trigger) {
        e.preventDefault();
        this.toggle();
        return;
      }

      // 2. Check if clicking close target
      if (this.isOpen) {
        const closeBtn = e.target.closest(this.options.closeSelector);
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }
      }
    });
  }
}

// Expose globally
window.Drawer = Drawer;
